// src/components/artistDashboard/labelServices/HotRatersTargetModal.js
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../../../context/ProfileContext';

// 🎨 CONFIG: Pastel background & matching border color matrices
const colourRegistry = {
    'share_direct_url': { 
        bg: 'bg-[#e9d5ff]/70',      // Soft Purple
        border: 'border-[#c084fc]', 
        darkBg: 'bg-[#581c87]/25', 
        darkBorder: 'border-[#a855f7]/40'
    },
    'publish_announcement': { 
        bg: 'bg-[#bbf7d0]/70',     // Pale Green
        border: 'border-[#4ade80]', 
        darkBg: 'bg-[#14532d]/25', 
        darkBorder: 'border-[#22c55e]/40'
    },
    'share_existing_announcement': { 
        bg: 'bg-[#fed7aa]/70',     // Soft Orange
        border: 'border-[#fb923c]', 
        darkBg: 'bg-[#7c2d12]/25', 
        darkBorder: 'border-[#f97316]/40'
    },
    'boost_press_hub': { 
        bg: 'bg-[#fecdd3]/70',     // Light Rose
        border: 'border-[#fb7185]', 
        darkBg: 'bg-[#881337]/25', 
        darkBorder: 'border-[#f43f5e]/40'
    },
    'boost_trends_algorithm': { 
        bg: 'bg-[#fef08a]/70',     // Pale Yellow
        border: 'border-[#facc15]', 
        darkBg: 'bg-[#713f12]/25', 
        darkBorder: 'border-[#eab308]/40'
    },
    'amplified_radio_rotation': { 
        bg: 'bg-[#bae6fd]/70',     // Soft Sky Blue
        border: 'border-[#38bdf8]', 
        darkBg: 'bg-[#0c4a6e]/25', 
        darkBorder: 'border-[#0ea5e9]/40'
    }
};

const HotRatersTargetModal = ({
    isOpen,
    onClose,
    onActionClick
}) => {
    const { isDarkMode } = useProfile();

    const targetOptions = [
        { id: 'share_direct_url', title: 'Share Direct Track URL', isPaid: false },
        { id: 'publish_announcement', title: 'Publish An Announcement', isPaid: false },
        { id: 'share_existing_announcement', title: 'Share Active Release URL', isPaid: false },
        { id: 'boost_press_hub', title: 'Press Hub Spotlight', isPaid: true },
        { id: 'boost_trends_algorithm', title: 'Trends Discovery Priority', isPaid: true },
        { id: 'amplified_radio_rotation', title: 'Amplified Radio Rotation', isPaid: true }
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
                    <motion.div
                        initial={{ scale: 0.93, y: 15 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.93, opacity: 0, y: 15 }}
                        transition={{ type: "spring", duration: 0.4 }}
                        className={`rounded-2xl shadow-2xl w-full max-w-md flex flex-col max-h-[85vh] overflow-hidden border transition-all duration-300 ${
                            isDarkMode
                                ? "bg-zinc-900 border-zinc-800 text-white shadow-black/60"
                                : "bg-white border-slate-100 text-slate-900 shadow-slate-200"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header Context Bar Container */}
                        <div className={`p-5 border-b flex-shrink-0 flex items-center justify-between ${isDarkMode ? 'border-zinc-800 bg-zinc-950/20' : 'border-slate-100 bg-slate-50/50'}`}>
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-wider dark:text-white leading-tight">
                                    Get More Hot Raters
                                </h3>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-7 h-7 rounded-full bg-slate-100 dark:bg-zinc-800 flex items-center justify-center font-bold text-xs text-slate-500 dark:text-zinc-400 hover:text-brand-pink transition-colors focus:outline-none"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Options Stack Grid */}
                        <div className="flex-grow overflow-y-auto p-5 custom-scrollbar min-h-0 space-y-3">
                            {targetOptions.map((option) => {
                                const colRef = colourRegistry[option.id];

                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            onClose();
                                            if (onActionClick) onActionClick(option.id);
                                        }}
                                        className={`w-full text-left p-4 rounded-2xl border flex items-center justify-between group h-16 transition-all duration-200 shadow-sm active:scale-98 focus:outline-none relative overflow-hidden ${
                                            isDarkMode 
                                                ? `${colRef.darkBg} ${colRef.darkBorder} hover:brightness-125` 
                                                : `${colRef.bg} ${colRef.border} hover:brightness-105`
                                        }`}
                                    >
                                        {/* Left Side: Text strictly in Capital Case Layout */}
                                        <div className="pr-20 overflow-hidden">
                                            <h5 className="text-[11px] font-black uppercase tracking-wider leading-none truncate dark:text-white text-slate-900">
                                                {option.title}
                                            </h5>
                                        </div>
                                        
                                        {/* 🌟 UPGRADED: Top-Right Segmented Visibility Badge */}
                                        <span className={`absolute top-2 right-2 text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded leading-none font-mono ${
                                            isDarkMode 
                                                ? option.isPaid 
                                                    ? 'bg-amber-950/60 text-amber-400 border-none' // Contrast fill for Paid
                                                    : 'bg-zinc-900/90 text-zinc-400 border-none'    // Contrast fill for Free
                                                : `${colRef.bg} ${colRef.border} text-slate-800 border` // Standard Light Mode border logic
                                        }`}>
                                            {option.isPaid ? 'Paid' : 'Free'}
                                        </span>
                                        
                                        {/* 🌟 UPGRADED: Perfectly centered + icon marker on the far right */}
                                        <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-xl font-bold leading-none h-5 flex items-center justify-center transition-colors ${
                                            isDarkMode ? 'text-zinc-500 group-hover:text-white' : 'text-slate-400 group-hover:text-slate-900'
                                        }`}>
                                            +
                                        </span>

                                    </button>
                                );
                            })}
                        </div>

                        {/* Modal Footer Spacer Row */}
                        <div className={`p-4 flex justify-center items-center border-t flex-shrink-0 ${isDarkMode ? 'bg-zinc-950/40 border-zinc-800' : 'bg-slate-50 border-slate-100'}`}>
                            <button
                                onClick={onClose}
                                className={`text-[10px] font-black uppercase tracking-widest transition-colors focus:outline-none ${
                                    isDarkMode ? "text-zinc-500 hover:text-white" : "text-slate-400 hover:text-slate-700"
                                }`}
                            >
                                Close Console
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default HotRatersTargetModal;